import { useState } from 'react';

// Minimal Btn for PrivacyModal close button
function Btn({ onClick, children, style={} }) {
  return (
    <button onClick={onClick} style={{
      fontFamily:"'Share Tech Mono','Courier New',monospace", border:'none', cursor:'pointer',
      borderRadius:3, padding:'.4rem .8rem', fontSize:'.72rem', background:'transparent',
      color:'#7a7872', letterSpacing:'.06em', textTransform:'uppercase', ...style
    }}>{children}</button>
  );
}

// useIsMobile hook (duplicated for standalone use)
function useIsMobile() {
  const [m, setM] = useState(()=>window.innerWidth < 640);
  return m;
}

function PrivacyModal({ onClose }) {
  const isMob = useIsMobile();
  const wrapStyle = isMob
    ? { position:'fixed', inset:0, zIndex:1100 }
    : { position:'fixed', inset:0, background:'rgba(0,0,0,.88)', backdropFilter:'blur(6px)', zIndex:1100, display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem' };
  const containerStyle = isMob
    ? { position:'fixed', inset:0, background:'#18181a', display:'flex', flexDirection:'column', overflowY:'auto', paddingBottom:'env(safe-area-inset-bottom,1.5rem)' }
    : { background:'#18181a', border:'1px solid #2e2e33', borderRadius:4, width:'100%', maxWidth:560, maxHeight:'88vh', overflowY:'auto', boxShadow:'0 24px 80px rgba(0,0,0,.9)' };
  const p = { fontSize:'.74rem', color:'#7a7872', lineHeight:1.8, marginBottom:'.75rem' };
  const h = { fontFamily:"'Black Ops One',sans-serif", fontSize:'.82rem', color:'#c0bdb6', marginBottom:'.4rem', marginTop:'1.2rem', letterSpacing:'.04em' };
  const D = { height:1, background:'#1e1e20', margin:'1rem 0' };
  const EFFECTIVE = 'March 9, 2025';

  return (
    <div style={wrapStyle} onClick={isMob ? undefined : e=>{ if(e.target===e.currentTarget) onClose(); }}>
      <div style={containerStyle}>
        {/* Header */}
        <div style={{ padding:isMob?'1rem':'1.1rem 1.4rem', borderBottom:'1px solid #2e2e33', background:'#1f1f22',
          display:'flex', justifyContent:'space-between', alignItems:'center', position:'sticky', top:0,
          paddingTop:isMob?'calc(env(safe-area-inset-top,0px) + 1rem)':'1.1rem' }}>
          <div style={{ fontFamily:"'Black Ops One',sans-serif", fontSize:'1.1rem', color:'#f0ede6' }}>
            Privacy <span style={{ color:'#0eb8a0' }}>Policy</span>
          </div>
          <Btn variant="ghost" onClick={onClose} style={{ fontSize:'1.1rem', minWidth:44, minHeight:44 }}>✕</Btn>
        </div>

        <div style={{ padding:isMob?'1.1rem 1rem':'1.35rem 1.4rem' }}>
          <p style={{ ...p, color:'#5a5a5a', fontSize:'.65rem' }}>Effective date: {EFFECTIVE}</p>

          <p style={p}>
            GearVault ("we", "us", or "our") operates gearvault.xyz. This policy describes what information
            we collect, how we use it, and the rights you have over it. By using GearVault, you agree to the
            practices described here.
          </p>

          <div style={D}/>

          <div style={h}>1. Information We Collect</div>
          <p style={p}>
            <strong style={{ color:'#c0bdb6' }}>Account information.</strong> When you create an account,
            we collect your email address and a hashed password. We never store your password in plain text.
          </p>
          <p style={p}>
            <strong style={{ color:'#c0bdb6' }}>Gear inventory data.</strong> All gear records you create —
            including make, model, photos, notes, valuations, serial numbers, and tags — are stored in your
            private vault and associated with your account.
          </p>
          <p style={p}>
            <strong style={{ color:'#c0bdb6' }}>Usage data.</strong> We do not use analytics services,
            advertising trackers, or third-party data collection of any kind. We do not log individual
            page views or user actions beyond what is necessary to operate the service.
          </p>

          <div style={D}/>

          <div style={h}>2. How We Use Your Information</div>
          <p style={p}>
            We use the information you provide solely to operate GearVault for you. Specifically:
          </p>
          {[
            'To authenticate your identity and secure access to your vault.',
            'To store, retrieve, and display your gear inventory.',
            'To enable features you explicitly use, such as Reverb.com market lookups.',
            'To send transactional emails, such as password reset links, when you request them.',
          ].map((item, i) => (
            <p key={i} style={{ ...p, paddingLeft:'1rem', marginBottom:'.4rem' }}>• {item}</p>
          ))}
          <p style={{ ...p, marginTop:'.6rem' }}>
            <strong style={{ color:'#c0bdb6' }}>We will never use your data for any other purpose.</strong> We
            do not analyze your inventory, sell insights, run ads, or share your information with any third party
            for any reason.
          </p>

          <div style={D}/>

          <div style={h}>3. Data Sharing</div>
          <p style={p}>
            We do not sell, rent, trade, or otherwise share your personal data with any third party, ever.
            Your gear inventory is private to your account. No other user, company, advertiser, or AI system
            has access to your data.
          </p>
          <p style={p}>
            The only third-party service your data touches is our database infrastructure provider (Supabase),
            which stores your data under strict data processing agreements and does not use it for any purpose
            beyond storage and retrieval on our behalf.
          </p>

          <div style={D}/>

          <div style={h}>4. Data Retention</div>
          <p style={p}>
            Your data is retained for as long as your account is active. If you delete your account,
            all associated data — including your gear inventory, photos, and account credentials — is
            permanently deleted within 30 days. We do not retain backups of deleted user data beyond
            that window.
          </p>

          <div style={D}/>

          <div style={h}>5. Security</div>
          <p style={p}>
            All data is transmitted over HTTPS. Passwords are hashed using industry-standard algorithms
            and are never stored in recoverable form. Access to your vault is protected by authenticated
            sessions using secure tokens. We take reasonable technical measures to protect your data
            against unauthorized access.
          </p>

          <div style={D}/>

          <div style={h}>6. Your Rights</div>
          <p style={p}>You have the right to:</p>
          {[
            'Access all data stored in your account at any time by logging in.',
            'Export your gear inventory at any time using the Export Vault feature.',
            'Request deletion of your account and all associated data.',
            'Correct any inaccurate information in your account.',
          ].map((item, i) => (
            <p key={i} style={{ ...p, paddingLeft:'1rem', marginBottom:'.4rem' }}>• {item}</p>
          ))}
          <p style={{ ...p, marginTop:'.6rem' }}>
            To exercise any of these rights, contact us at the address below.
          </p>

          <div style={D}/>

          <div style={h}>7. Cookies</div>
          <p style={p}>
            GearVault uses browser localStorage to store your authentication session token locally on your
            device. This is strictly functional and is never used for tracking or advertising purposes.
            We do not use cookies or any other tracking technologies.
          </p>

          <div style={D}/>

          <div style={h}>8. Children's Privacy</div>
          <p style={p}>
            GearVault is not directed at children under the age of 13. We do not knowingly collect
            personal information from children. If you believe a child has created an account, please
            contact us and we will delete it promptly.
          </p>

          <div style={D}/>

          <div style={h}>9. Changes to This Policy</div>
          <p style={p}>
            We may update this policy from time to time. Any changes will be posted on this page with
            an updated effective date. Your continued use of GearVault after changes constitutes acceptance
            of the updated policy.
          </p>

          <div style={D}/>

          <div style={h}>10. Contact</div>
          <p style={p}>
            If you have questions or concerns about this privacy policy, or wish to exercise your data rights,
            please contact us at: <span style={{ color:'#0eb8a0' }}>privacy@gearvault.xyz</span>
          </p>

          <div style={{ height:1, background:'#1e1e20', margin:'1.5rem 0 1rem' }}/>
          <p style={{ fontSize:'.58rem', color:'#2e2e33', textAlign:'center', lineHeight:1.8 }}>
            GearVault · gearvault.xyz<br/>
            This policy is provided in plain English, not legalese, because we believe you deserve to understand it.
          </p>
        </div>
      </div>
    </div>
  );
}

export default PrivacyModal;
